import { Paper, PaperProps } from '@mui/material';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { aiAssistantContext } from '../../../state/AIAssistantContextProvider';
import Draggable from 'react-draggable';
import { DIALOG_WIDTH } from './AIAssistantDialog';
import { calculateDraggableBoundaries } from './utils';

const AIAssistantDraggable = (props : PaperProps) => {
  const nodeRef = React.useRef<HTMLDivElement>(null);
  
  const { 
    assistantPosition, 
    setAssistantPosition, 
    draggableBounds,
    setDraggableBounds,
    isExtended
  } = useContext(aiAssistantContext);
  const lastIsExtendedState = useRef(isExtended);
  
  const recalculateDraggableBounds = useCallback(() => {
    if (!nodeRef.current) return;
  
    const { offsetHeight, offsetWidth } = nodeRef.current;
    const calculatedDraggableBounds = calculateDraggableBoundaries({
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      elementWidth: offsetWidth,
      elementHeight: offsetHeight
    });

    setDraggableBounds(calculatedDraggableBounds);
  }, [setDraggableBounds]);
  
  useEffect(() => {
    // calculate initial draggable bounds
    const areDraggableBoundsSet = !Object.values(draggableBounds).every(value => value === 0);
    if (nodeRef.current && !areDraggableBoundsSet) {
      recalculateDraggableBounds();
    }
  }, [draggableBounds, recalculateDraggableBounds]);
  
  useEffect(() => {
    // make sure the draggable dialog is not out of the screen on resize
    if (!nodeRef.current) return;
    if (lastIsExtendedState.current === isExtended) return;
  
    const { offsetWidth } = nodeRef.current;
    const widthDifference = DIALOG_WIDTH.EXTENDED - DIALOG_WIDTH.SLIM;
    const newLeftBound = 
      draggableBounds.left + (offsetWidth === DIALOG_WIDTH.SLIM ? -widthDifference : widthDifference);
    setDraggableBounds({ ...draggableBounds, left: newLeftBound });
    lastIsExtendedState.current = isExtended;
  }, [isExtended, draggableBounds, assistantPosition, setAssistantPosition, setDraggableBounds]);
  
  useEffect(() => {
    // make sure the draggable dialog is not dragged out of the screen
    if (assistantPosition.x < draggableBounds.left) {
      setAssistantPosition({ ...assistantPosition, x: draggableBounds.left });
    }
  }, [draggableBounds, assistantPosition, setAssistantPosition]);
  
  useEffect(() => {
    window.addEventListener('resize', recalculateDraggableBounds);
    return () => {
      window.removeEventListener('resize', recalculateDraggableBounds);
    };
  }, [recalculateDraggableBounds]);
  
  return (
    <Draggable
      nodeRef={nodeRef as React.RefObject<HTMLDivElement>}
      handle='#draggable-dialog-title'
      cancel={'[class*="MuiDialogContent-root"]'}
      bounds={draggableBounds}
      onStop={(_, { x,y }) => setAssistantPosition({ x, y })}
      position={assistantPosition}
      enableUserSelectHack
    >
      <Paper data-testid='ai-assistant-draggable' {...props} ref={nodeRef} />
    </Draggable>
  );
};

export default AIAssistantDraggable;
